import { Link, Tooltip } from "@heroui/react";
import { Debug } from "@sc07-canvas/lib/debug";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  Cog,
  Crop,
  Eye,
  Grid2x2,
  Hammer,
  Info,
  Palette as PaletteIcon,
  Pipette,
  Scan,
  Settings,
  Share2,
  Wrench,
  X,
} from "lucide-react";
import {
  type CSSProperties,
  type PointerEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import useSWR from "swr";

import { useAppContext } from "../../contexts/AppContext";
import { useCaptureContext } from "../../contexts/CaptureContext";
import { useDialog } from "../../contexts/DialogContext";
import { usePanel } from "../../contexts/PanelContext";
import { useTemplateContext } from "../../contexts/TemplateContext";
import {
  PALETTE_LAYOUT_SPRING,
  useCompactPalette,
} from "../../hooks/useCompactPalette";
import { useHasRole } from "../../hooks/useHasRole";
import { useIsMobile } from "../../hooks/useIsMobile";
import { CanvasCore } from "../../lib/canvas";
import { KeybindManager } from "../../lib/keybinds";
import { PaletteLib } from "../../lib/palette";
import {
  Template as TemplateRenderer,
  TEMPLATE_STYLE_COLUMNS,
  TEMPLATE_STYLE_ROWS,
  TemplateStyle,
} from "../../lib/template";
import { useModerator } from "../../Moderator/Moderator";
import OpenChatButton from "../Chat/OpenChatButton";
import {
  showColorDeselectFeedback,
  showColorPickFeedback,
} from "../ColorPickFeedback";
import { Button } from "../core/Button";
import { ThemeSwitcher } from "../Header/ThemeSwitcher";
import { LoginButton } from "../Login/LoginButton";

const PaletteColorSurface = ({ hex }: { hex: string }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const context = canvasRef.current?.getContext("2d");
    if (!context) return;

    context.fillStyle = `#${hex}`;
    context.fillRect(0, 0, 1, 1);
  }, [hex]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pallete-color-surface"
      data-darkreader-ignore
      height={1}
      width={1}
    />
  );
};

const getPaletteSymbolBackgroundPosition = (index: number) => {
  const column = index % TEMPLATE_STYLE_COLUMNS;
  const row = Math.floor(index / TEMPLATE_STYLE_COLUMNS) % TEMPLATE_STYLE_ROWS;
  const x = (column / (TEMPLATE_STYLE_COLUMNS - 1)) * 100;
  const y = (row / (TEMPLATE_STYLE_ROWS - 1)) * 100;

  return `${x}% ${y}%`;
};

const parseHexColor = (hex: string) => {
  const rgb = parseInt(hex, 16);
  return {
    red: (rgb >> 16) & 0xff,
    green: (rgb >> 8) & 0xff,
    blue: rgb & 0xff,
  };
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const rgbToHsl = ({ red, green, blue }: ReturnType<typeof parseHexColor>) => {
  const r = red / 255;
  const g = green / 255;
  const b = blue / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const lightness = (max + min) / 2;

  if (delta === 0) {
    return { hue: 0, saturation: 0, lightness };
  }

  const saturation =
    lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  let hue = 0;

  switch (max) {
    case r:
      hue = (g - b) / delta + (g < b ? 6 : 0);
      break;
    case g:
      hue = (b - r) / delta + 2;
      break;
    case b:
      hue = (r - g) / delta + 4;
      break;
  }

  return { hue: hue * 60, saturation, lightness };
};

const hslToCss = (hue: number, saturation: number, lightness: number) =>
  `hsl(${Math.round(hue)} ${Math.round(saturation * 100)}% ${Math.round(
    lightness * 100,
  )}%)`;

const getPaletteSymbolColor = (hex: string) => {
  const { hue, saturation, lightness } = rgbToHsl(parseHexColor(hex));

  if (saturation < 0.08) {
    if (lightness < 0.14) return hslToCss(0, 0, 0.56);
    if (lightness > 0.86) return hslToCss(0, 0, 0.42);

    return hslToCss(
      0,
      0,
      clamp(lightness > 0.52 ? lightness - 0.34 : lightness + 0.34, 0.22, 0.78),
    );
  }

  const symbolLightness =
    lightness > 0.62
      ? lightness - 0.42
      : lightness > 0.5
        ? lightness - 0.34
        : lightness < 0.28
          ? lightness + 0.42
          : lightness < 0.4
            ? lightness + 0.36
            : lightness + 0.3;

  return hslToCss(
    hue,
    clamp(saturation * 1.12, 0.38, 0.96),
    clamp(symbolLightness, 0.14, 0.84),
  );
};

const getPaletteSymbolStyle = (index: number, hex: string): CSSProperties => {
  const backgroundPosition = getPaletteSymbolBackgroundPosition(index);
  const backgroundSize = `${TEMPLATE_STYLE_COLUMNS * 100}% ${TEMPLATE_STYLE_ROWS * 100}%`;

  return {
    backgroundColor: getPaletteSymbolColor(hex),
    maskImage: `url(${TemplateStyle.SYMBOLS})`,
    maskPosition: backgroundPosition,
    maskSize: backgroundSize,
    WebkitMaskImage: `url(${TemplateStyle.SYMBOLS})`,
    WebkitMaskPosition: backgroundPosition,
    WebkitMaskSize: backgroundSize,
  };
};

type PickColorTarget = "primary" | "secondary";
type PickColorPreview = {
  clientX: number;
  clientY: number;
  hex: string;
  name: string;
};

export const Palette = () => {
  const reduceMotion = useReducedMotion() ?? false;
  const paletteLayoutTransition = reduceMotion
    ? { duration: 0 }
    : PALETTE_LAYOUT_SPRING;
  const { config, user, cursor, settings } = useAppContext<true>();
  const Dialog = useDialog();
  const Panel = usePanel();
  const { state: moderatorState, dispatch: moderatorDispatch } = useModerator();
  const {
    captureCanvas,
    captureView,
    captureRegion,
    captureTemplateArea,
    hasTemplateCapture,
  } = useCaptureContext();
  const hasAdmin = useHasRole("ADMIN");
  const hasMod = useHasRole("MOD");
  const compactPalette = useCompactPalette();
  const isMobile = useIsMobile();
  const template = useTemplateContext();
  const meta = useSWR<{ login: "INHIBIT" | "NORMAL" | "TRUST" }>("/api/info");
  const [templateColorCounts, setTemplateColorCounts] = useState<
    Map<number, number>
  >(() => new Map());
  const [pickColorMode, setPickColorMode] = useState(false);
  const [pickColorPreview, setPickColorPreview] =
    useState<PickColorPreview | null>(null);
  const [mobilePaletteOpen, setMobilePaletteOpen] = useState(false);
  const [mobileActionSheet, setMobileActionSheet] = useState<"more" | null>(
    null,
  );
  const hasActiveTemplate = template.enable && !!template.url;
  const shouldUseTemplateColorCounts =
    hasActiveTemplate &&
    (settings.get["template.hideUnusedColors"] ||
      settings.get["template.pixelCounts"]);

  const visiblePaletteColors = useMemo(() => {
    const colors = config.pallete.colors.map((color, index) => ({
      color,
      index,
      needed: templateColorCounts.get(color.id) ?? 0,
    }));

    if (
      !shouldUseTemplateColorCounts ||
      !settings.get["template.hideUnusedColors"]
    ) {
      return colors;
    }

    return colors.filter(
      ({ color, needed }) =>
        needed > 0 ||
        color.id === cursor.color ||
        color.id === cursor.secondaryColor,
    );
  }, [
    config.pallete.colors,
    cursor.color,
    cursor.secondaryColor,
    shouldUseTemplateColorCounts,
    settings.get["template.hideUnusedColors"],
    templateColorCounts,
  ]);

  const selectedPaletteColor = useMemo(
    () => config.pallete.colors.find((color) => color.id === cursor.color),
    [config.pallete.colors, cursor.color],
  );

  const mobileAccountName =
    user?.user.display_name || user?.user.username || "User";
  const mobileAccountInitial = user
    ? mobileAccountName.trim().charAt(0).toUpperCase() || "?"
    : "?";

  const handleSelectPrimaryColor = (color: number) => {
    if (cursor.color === color) {
      PaletteLib.setSelectedColor(undefined);
      return;
    }

    if (cursor.secondaryColor === color) {
      PaletteLib.setSecondaryColor(undefined);
    }

    PaletteLib.setSelectedColor(color);
  };

  const handleSelectSecondaryColor = (color: number) => {
    if (cursor.secondaryColor === color) {
      PaletteLib.setSecondaryColor(undefined);
      return;
    }

    if (cursor.color === color) {
      PaletteLib.setSelectedColor(undefined);
    }

    PaletteLib.setSecondaryColor(color);
  };

  const handleSelectMobileColor = (color: number) => {
    PaletteLib.setSecondaryColor(undefined);
    PaletteLib.setSelectedColor(color);
    setMobilePaletteOpen(false);
  };

  const handleMobileCaptureCanvas = () => {
    setMobileActionSheet(null);
    captureCanvas();
  };

  const handleMobileCaptureView = () => {
    setMobileActionSheet(null);
    captureView();
  };

  const handleMobileCaptureRegion = () => {
    setMobileActionSheet(null);
    captureRegion();
  };

  const handleMobileCaptureTemplateArea = () => {
    setMobileActionSheet(null);
    captureTemplateArea();
  };

  const isCoordInCanvas = (x: number, y: number) =>
    x >= 0 && y >= 0 && x < config.canvas.size[0] && y < config.canvas.size[1];

  const getTemplatePixelColor = (clientX: number, clientY: number) => {
    const renderer = TemplateRenderer.instance;

    if (!template.enable || !renderer) {
      return -1;
    }

    return renderer.getPaletteColorAt(clientX, clientY) ?? -1;
  };

  const getPickedColor = (clientX: number, clientY: number) => {
    const canvas = CanvasCore.get(false);

    if (!canvas) {
      console.warn("[Palette#getPickedColor] canvas instance does not exist");
      return;
    }

    let x: number;
    let y: number;

    try {
      [x, y] = canvas.screenToPos(clientX, clientY);
    } catch (error) {
      console.warn(
        "[Palette#getPickedColor] could not convert screen position",
        error,
      );
      return;
    }

    const templateColor = getTemplatePixelColor(clientX, clientY);
    if (templateColor !== -1) return templateColor;

    if (!isCoordInCanvas(x, y)) return null;

    const canvasColor = canvas.getPixel(x, y)?.color;
    if (canvasColor !== undefined && canvasColor >= 0) return canvasColor;

    return PaletteLib.getColorFromHex("ffffff")?.id;
  };

  const handlePickColorAt = (
    clientX: number,
    clientY: number,
    target: PickColorTarget,
  ) => {
    const pickedColor = getPickedColor(clientX, clientY);
    if (pickedColor === undefined) return undefined;

    if (pickedColor === null) {
      if (target === "secondary") {
        PaletteLib.setSecondaryColor(undefined);
      } else {
        PaletteLib.setSelectedColor(undefined);
      }
      return null;
    }

    if (target === "secondary") {
      if (cursor.color === pickedColor) {
        PaletteLib.setSelectedColor(undefined);
      }
      PaletteLib.setSecondaryColor(pickedColor);
      return pickedColor;
    }

    if (cursor.secondaryColor === pickedColor) {
      PaletteLib.setSecondaryColor(undefined);
    }
    PaletteLib.setSelectedColor(pickedColor);
    return pickedColor;
  };

  useEffect(() => {
    const renderer = TemplateRenderer.instance;
    const canvas = CanvasCore.get();

    const refreshTemplateColorCounts = () => {
      if (
        !shouldUseTemplateColorCounts ||
        !renderer ||
        !canvas ||
        template.x === undefined ||
        template.y === undefined
      ) {
        setTemplateColorCounts(new Map());
        return;
      }

      setTemplateColorCounts(
        renderer.getNeededPaletteColorCounts({
          x: template.x,
          y: template.y,
          canvasWidth: config.canvas.size[0],
          canvasHeight: config.canvas.size[1],
          getCanvasPixelColor: (x, y) => canvas.getPixel(x, y)?.color,
        }),
      );
    };

    refreshTemplateColorCounts();

    if (!shouldUseTemplateColorCounts || !renderer || !canvas) return;

    renderer.on("paletteColorCounts", refreshTemplateColorCounts);
    canvas.on("pixelsChanged", refreshTemplateColorCounts);

    return () => {
      renderer.off("paletteColorCounts", refreshTemplateColorCounts);
      canvas.off("pixelsChanged", refreshTemplateColorCounts);
    };
  }, [
    config.canvas.size[0],
    config.canvas.size[1],
    shouldUseTemplateColorCounts,
    template.url,
    template.width,
    template.x,
    template.y,
  ]);

  useEffect(() => {
    // TODO: move keybind listening to specified module
    const handleDeselect = () => {
      PaletteLib.setSelectedColor(undefined);
    };

    const handleSwapColors = () => {
      PaletteLib.swapSelectedColors();
    };

    const handlePickColorKeybind = () => {
      setPickColorPreview(null);
      setPickColorMode(true);
    };

    KeybindManager.addListener("DESELECT_COLOR", handleDeselect);
    KeybindManager.addListener("SWAP_COLORS", handleSwapColors);
    KeybindManager.addListener("PICK_COLOR", handlePickColorKeybind);

    return () => {
      KeybindManager.removeListener("DESELECT_COLOR", handleDeselect);
      KeybindManager.removeListener("SWAP_COLORS", handleSwapColors);
      KeybindManager.removeListener("PICK_COLOR", handlePickColorKeybind);
    };
  }, []);

  useEffect(() => {
    if (!pickColorMode || typeof document === "undefined") return;

    document.body.classList.add("is-picking-color");

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setPickColorMode(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.classList.remove("is-picking-color");
    };
  }, [pickColorMode]);

  useEffect(() => {
    if (!isMobile) {
      setMobilePaletteOpen(false);
      setMobileActionSheet(null);
    }
  }, [isMobile]);

  useEffect(() => {
    if (isMobile && cursor.secondaryColor !== undefined) {
      PaletteLib.setSecondaryColor(undefined);
    }
  }, [cursor.secondaryColor, isMobile]);

  const finishPickColor = (
    clientX: number,
    clientY: number,
    target: PickColorTarget,
  ) => {
    const pickedColor = handlePickColorAt(clientX, clientY, target);
    if (pickedColor === null) {
      showColorDeselectFeedback(clientX, clientY);
    } else if (typeof pickedColor === "number") {
      showColorPickFeedback(
        clientX,
        clientY,
        PaletteLib.getColor(pickedColor)?.hex,
      );
    }
    setPickColorMode(false);
  };

  const handlePickColorPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (event.button === 1 || event.button === 2) {
      window.addEventListener(
        event.button === 1 ? "auxclick" : "contextmenu",
        (followUpEvent) => followUpEvent.preventDefault(),
        { capture: true, once: true },
      );
    }

    finishPickColor(
      event.clientX,
      event.clientY,
      event.button === 2 ? "secondary" : "primary",
    );
  };

  const handlePickColorPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const pickedColor = getPickedColor(event.clientX, event.clientY);
    const paletteColor = config.pallete.colors.find(
      (color) => color.id === pickedColor,
    );

    if (!paletteColor) {
      setPickColorPreview(null);
      return;
    }

    setPickColorPreview({
      clientX: event.clientX,
      clientY: event.clientY,
      hex: paletteColor.hex,
      name: paletteColor.name,
    });
  };

  useEffect(() => {
    if (!pickColorMode || !isMobile) return;

    const panZoom = CanvasCore.get(false)?.getPanZoom();
    if (!panZoom) return;

    const handleMobilePick = ({
      clientX,
      clientY,
    }: {
      clientX: number;
      clientY: number;
    }) => finishPickColor(clientX, clientY, "primary");

    panZoom.on("click", handleMobilePick);
    return () => panZoom.off("click", handleMobilePick);
  });

  const pickColorOverlay =
    typeof document === "undefined"
      ? null
      : createPortal(
          <AnimatePresence>
            {pickColorMode && (
              <motion.div
                key="pick-color"
                animate={{ opacity: 1 }}
                aria-label="Pick color mode"
                className={[
                  isMobile ? "pointer-events-none" : "pointer-events-auto",
                  "fixed inset-0 z-[10000] cursor-crosshair overflow-hidden",
                ].join(" ")}
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                onAuxClick={(event) => event.preventDefault()}
                onContextMenu={(event) => event.preventDefault()}
                onPointerDown={handlePickColorPointerDown}
                onPointerMove={handlePickColorPointerMove}
                role="application"
                style={{ background: "rgba(14, 165, 233, 0.05)" }}
                transition={{ duration: reduceMotion ? 0 : 0.12 }}
              >
                <div className="canvas-overlay-card pointer-events-none absolute left-4 top-4 z-10 px-3 py-2">
                  <div className="flex items-center gap-2 text-sm font-semibold leading-none">
                    <Pipette aria-hidden="true" size={18} />
                    <span>Pick Color</span>
                  </div>
                  <div className="mt-1 text-xs text-black/65 dark:text-white/65">
                    {isMobile ? "Tap" : "Click"} on a pixel to copy its color to
                    your brush.
                  </div>
                </div>
                {pickColorPreview && (
                  <div
                    className="canvas-overlay-card pick-color-cursor-preview"
                    style={{
                      left:
                        pickColorPreview.clientX +
                        (pickColorPreview.clientX > window.innerWidth - 190
                          ? -16
                          : 16),
                      top:
                        pickColorPreview.clientY +
                        (pickColorPreview.clientY > window.innerHeight - 72
                          ? -16
                          : 16),
                      transform:
                        "translate(" +
                        (pickColorPreview.clientX > window.innerWidth - 190
                          ? "-100%"
                          : "0") +
                        ", " +
                        (pickColorPreview.clientY > window.innerHeight - 72
                          ? "-100%"
                          : "0") +
                        ")",
                    }}
                  >
                    <span
                      aria-hidden="true"
                      className="pick-color-preview-swatch"
                      style={{
                        backgroundColor: "#" + pickColorPreview.hex,
                      }}
                    />
                    <span className="pick-color-preview-details">
                      <span className="pick-color-preview-name">
                        {pickColorPreview.name}
                      </span>
                      <span className="pick-color-preview-hex">
                        {"#" + pickColorPreview.hex}
                      </span>
                    </span>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        );

  const userOverlay = !user ? (
    <div className="pallete-user-overlay">
      {meta.isLoading ? (
        <em>Loading...</em>
      ) : meta.data?.login === "INHIBIT" ? (
        <>Logging in is disabled</>
      ) : (
        <div className="pallete-user-overlay-content">
          <span>You are not logged in</span>
          <LoginButton />
        </div>
      )}
    </div>
  ) : null;

  const mobilePaletteSheet =
    typeof document === "undefined"
      ? null
      : createPortal(
          <AnimatePresence>
            {isMobile && user && mobilePaletteOpen && (
              <motion.div
                key="mobile-palette-sheet-backdrop"
                animate={{ opacity: 1 }}
                className="mobile-palette-sheet-backdrop"
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                onClick={() => setMobilePaletteOpen(false)}
                transition={{ duration: reduceMotion ? 0 : 0.16 }}
              >
                <motion.div
                  animate={{ y: 0 }}
                  aria-label="Choose color"
                  aria-modal="true"
                  className="mobile-palette-sheet canvas-overlay-card"
                  exit={{ y: "100%" }}
                  initial={{ y: "100%" }}
                  onClick={(event) => event.stopPropagation()}
                  role="dialog"
                  transition={
                    reduceMotion ? { duration: 0 } : PALETTE_LAYOUT_SPRING
                  }
                >
                  <div className="mobile-palette-sheet-header">
                    <span>Choose Color</span>
                    <button
                      type="button"
                      className="mobile-palette-icon-button"
                      aria-label="Close color picker"
                      onClick={() => setMobilePaletteOpen(false)}
                    >
                      <X aria-hidden="true" size={20} />
                    </button>
                  </div>
                  <div className="mobile-palette-sheet-grid">
                    {visiblePaletteColors.map(({ color, index, needed }) => (
                      <button
                        key={color.id}
                        type="button"
                        aria-label={color.name}
                        aria-pressed={color.id === cursor.color}
                        className={[
                          "mobile-palette-sheet-color",
                          color.id === cursor.color && "active",
                        ]
                          .filter((a) => a)
                          .join(" ")}
                        onClick={() => handleSelectMobileColor(color.id)}
                      >
                        <PaletteColorSurface hex={color.hex} />
                        {settings.get["palette.colors"] && (
                          <span
                            aria-hidden="true"
                            className="pallete-color-number"
                          >
                            {index}
                          </span>
                        )}
                        {settings.get["palette.symbols"] && (
                          <span
                            aria-hidden="true"
                            className="pallete-color-symbol"
                            style={getPaletteSymbolStyle(index, color.hex)}
                          />
                        )}
                        {hasActiveTemplate &&
                          settings.get["template.pixelCounts"] &&
                          needed > 0 && (
                            <span
                              aria-hidden="true"
                              className="pallete-color-needed"
                            >
                              {needed}
                            </span>
                          )}
                      </button>
                    ))}
                    <button
                      type="button"
                      aria-label="Deselect Color"
                      className="mobile-palette-sheet-deselect-icon"
                      onClick={() => {
                        PaletteLib.setSelectedColor(undefined);
                        setMobilePaletteOpen(false);
                      }}
                    >
                      <X aria-hidden="true" size={34} />
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        );
  const mobileActionSheetPortal =
    typeof document === "undefined"
      ? null
      : createPortal(
          <AnimatePresence>
            {isMobile && user && mobileActionSheet && (
              <motion.div
                key="mobile-action-sheet-backdrop"
                animate={{ opacity: 1 }}
                className="mobile-palette-sheet-backdrop"
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                onClick={() => setMobileActionSheet(null)}
                transition={{ duration: reduceMotion ? 0 : 0.16 }}
              >
                <motion.div
                  animate={{ y: 0 }}
                  aria-label="Account and more actions"
                  className="mobile-action-sheet canvas-overlay-card"
                  exit={{ y: "100%" }}
                  initial={{ y: "100%" }}
                  onClick={(event) => event.stopPropagation()}
                  role="menu"
                  transition={
                    reduceMotion ? { duration: 0 } : PALETTE_LAYOUT_SPRING
                  }
                >
                  <div className="mobile-palette-sheet-header mobile-action-sheet-header">
                    <div className="mobile-action-sheet-account-heading">
                      <span
                        className="mobile-palette-avatar"
                        aria-hidden="true"
                      >
                        {user.user.picture_url ? (
                          <img src={user.user.picture_url} alt="" />
                        ) : (
                          <span>{mobileAccountInitial}</span>
                        )}
                      </span>
                      <span className="mobile-action-sheet-account-copy">
                        <strong>{mobileAccountName}</strong>
                        <span>{user.service.instance.hostname}</span>
                      </span>
                    </div>
                    <button
                      type="button"
                      className="mobile-palette-icon-button"
                      aria-label="Close account actions"
                      onClick={() => setMobileActionSheet(null)}
                    >
                      <X aria-hidden="true" size={20} />
                    </button>
                  </div>
                  <div className="mobile-action-sheet-items">
                    <Button
                      aria-pressed={Boolean(Panel.state.info)}
                      className="mobile-action-sheet-item"
                      onPress={() => {
                        setMobileActionSheet(null);
                        Panel.dispatch([
                          "info",
                          Panel.state.info ? undefined : true,
                        ]);
                      }}
                      role="menuitem"
                      size="sm"
                    >
                      <Info aria-hidden="true" size={16} />
                      <p>Info</p>
                    </Button>
                    <Button
                      aria-pressed={Boolean(Panel.state.settings)}
                      className="mobile-action-sheet-item"
                      onPress={() => {
                        setMobileActionSheet(null);
                        Panel.dispatch([
                          "settings",
                          Panel.state.settings ? undefined : true,
                        ]);
                      }}
                      role="menuitem"
                      size="sm"
                    >
                      <Settings aria-hidden="true" size={16} />
                      <p>Settings</p>
                    </Button>
                    {settings.get["ui.showThemeSwitcher"] && (
                      <ThemeSwitcher
                        className="mobile-action-sheet-item"
                        onPress={() => setMobileActionSheet(null)}
                        role="menuitem"
                        size="sm"
                      />
                    )}
                    <OpenChatButton
                      className="mobile-action-sheet-item"
                      onPress={() => setMobileActionSheet(null)}
                      role="menuitem"
                      size="sm"
                    />
                    {import.meta.env.DEV && (
                      <Button
                        className="mobile-action-sheet-item"
                        onPress={() => {
                          setMobileActionSheet(null);
                          Debug.openDebugTools();
                        }}
                        role="menuitem"
                        size="sm"
                      >
                        <Wrench aria-hidden="true" size={16} />
                        <p>Debug Tools</p>
                      </Button>
                    )}
                    <Button
                      className="mobile-action-sheet-item"
                      onPress={() => {
                        setMobileActionSheet(null);
                        Dialog.dispatch({
                          action: "OPEN_DIALOG",
                          dialog: { id: "SHARE_MODAL" },
                        });
                      }}
                      role="menuitem"
                      size="sm"
                    >
                      <Share2 aria-hidden="true" size={16} />
                      <p>Share Link</p>
                    </Button>
                    <Button
                      className="mobile-action-sheet-item"
                      onPress={handleMobileCaptureCanvas}
                      silent
                      role="menuitem"
                      size="sm"
                    >
                      <Grid2x2 aria-hidden="true" size={16} />
                      <p>Capture Canvas</p>
                    </Button>
                    <Button
                      className="mobile-action-sheet-item"
                      onPress={handleMobileCaptureView}
                      silent
                      role="menuitem"
                      size="sm"
                    >
                      <Eye aria-hidden="true" size={16} />
                      <p>Capture View</p>
                    </Button>
                    <Button
                      className="mobile-action-sheet-item"
                      onPress={handleMobileCaptureRegion}
                      role="menuitem"
                      size="sm"
                    >
                      <Scan aria-hidden="true" size={16} />
                      <p>Capture Region</p>
                    </Button>
                    {hasTemplateCapture && (
                      <Button
                        className="mobile-action-sheet-item"
                        onPress={handleMobileCaptureTemplateArea}
                        silent
                        role="menuitem"
                        size="sm"
                      >
                        <Crop aria-hidden="true" size={16} />
                        <p>Capture Template Area</p>
                      </Button>
                    )}
                    {hasMod && (
                      <Button
                        aria-pressed={Boolean(
                          moderatorState.showMenu || moderatorState.inspectUser,
                        )}
                        className="mobile-action-sheet-item"
                        onPress={() => {
                          setMobileActionSheet(null);
                          moderatorDispatch({ action: "OPEN_MENU" });
                        }}
                        role="menuitem"
                        size="sm"
                      >
                        <Hammer aria-hidden="true" size={16} />
                        <p>Mod</p>
                      </Button>
                    )}
                    {(hasAdmin || hasMod) && (
                      <Button
                        as={Link}
                        className="mobile-action-sheet-item"
                        href="/admin"
                        onPress={() => setMobileActionSheet(null)}
                        role="menuitem"
                        size="sm"
                        target="_blank"
                      >
                        <Cog aria-hidden="true" size={16} />
                        <p>Admin</p>
                      </Button>
                    )}
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        );

  if (isMobile) {
    return (
      <>
        {pickColorOverlay}
        {mobilePaletteSheet}
        {mobileActionSheetPortal}
        <motion.div
          id="pallete"
          className="pallete--mobile"
          layout={!reduceMotion}
          layoutDependency={selectedPaletteColor?.id ?? "none"}
          transition={paletteLayoutTransition}
        >
          <div className="mobile-palette-bar">
            <button
              type="button"
              className="mobile-palette-current"
              aria-label={
                selectedPaletteColor
                  ? `Choose color. Current color: ${selectedPaletteColor.name}`
                  : "Click to select a color"
              }
              onClick={() => {
                setMobileActionSheet(null);
                setMobilePaletteOpen(true);
              }}
            >
              {selectedPaletteColor ? (
                <span
                  className="mobile-palette-current-swatch"
                  aria-hidden="true"
                >
                  <PaletteColorSurface hex={selectedPaletteColor.hex} />
                </span>
              ) : (
                <span
                  className="mobile-palette-current-swatch mobile-palette-current-swatch--empty"
                  aria-hidden="true"
                />
              )}
              <span className="mobile-palette-current-text">
                {selectedPaletteColor?.name ?? "Click to select a color"}
              </span>
              <PaletteIcon aria-hidden="true" size={20} />
            </button>
            <button
              type="button"
              className="mobile-palette-icon-button"
              aria-label="Pick Color"
              onClick={() => {
                setPickColorPreview(null);
                setMobileActionSheet(null);
                setPickColorMode(true);
              }}
            >
              <Pipette aria-hidden="true" size={20} />
            </button>
            <button
              type="button"
              className="mobile-palette-icon-button mobile-palette-avatar-button"
              aria-expanded={mobileActionSheet === "more"}
              aria-haspopup="menu"
              aria-label="Account and more"
              onClick={() => {
                setMobilePaletteOpen(false);
                setMobileActionSheet((sheet) =>
                  sheet === "more" ? null : "more",
                );
              }}
            >
              <span className="mobile-palette-avatar" aria-hidden="true">
                {user?.user.picture_url ? (
                  <img src={user.user.picture_url} alt="" />
                ) : (
                  <span>{mobileAccountInitial}</span>
                )}
              </span>
            </button>
          </div>
          {userOverlay}
        </motion.div>
      </>
    );
  }

  return (
    <>
      {pickColorOverlay}
      <motion.div
        id="pallete"
        className={compactPalette ? "pallete--compact" : undefined}
        layout={!reduceMotion}
        layoutDependency={compactPalette}
        transition={paletteLayoutTransition}
      >
        <motion.div
          className="pallete-colors"
          layout={!reduceMotion}
          layoutDependency={compactPalette}
          transition={paletteLayoutTransition}
        >
          <Tooltip
            disableAnimation={reduceMotion}
            closeDelay={0}
            content="Deselect Color"
            delay={250}
            placement="top"
            classNames={{
              content: "canvas-overlay-card palette-tooltip px-2 py-1 text-xs",
            }}
          >
            <motion.button
              layout={!reduceMotion}
              layoutDependency={compactPalette}
              transition={paletteLayoutTransition}
              aria-label="Deselect Color"
              className="pallete-color--deselect"
              onClick={() => PaletteLib.setSelectedColor(undefined)}
              onContextMenu={(event) => {
                event.preventDefault();
                PaletteLib.setSecondaryColor(undefined);
              }}
            >
              <X aria-hidden="true" size={28} />
            </motion.button>
          </Tooltip>
          <motion.div
            layout={!reduceMotion}
            layoutDependency={compactPalette}
            transition={paletteLayoutTransition}
            className="pallete-color-grid"
            style={
              {
                "--regular-palette-columns": visiblePaletteColors.length,
                "--compact-palette-columns": Math.ceil(
                  visiblePaletteColors.length / 2,
                ),
              } as CSSProperties
            }
          >
            {visiblePaletteColors.map(({ color, index, needed }) => (
              <Tooltip
                disableAnimation={reduceMotion}
                key={color.id}
                closeDelay={0}
                content={color.name}
                delay={250}
                placement="top"
                classNames={{
                  content:
                    "canvas-overlay-card palette-tooltip px-2 py-1 text-xs",
                }}
              >
                <motion.button
                  layout={!reduceMotion}
                  layoutDependency={compactPalette}
                  transition={paletteLayoutTransition}
                  aria-label={color.name}
                  className={[
                    "pallete-color",
                    color.id === cursor.color && "active",
                    color.id === cursor.secondaryColor && "secondary",
                  ]
                    .filter((a) => a)
                    .join(" ")}
                  onClick={() => {
                    handleSelectPrimaryColor(color.id);
                  }}
                  onContextMenu={(event) => {
                    event.preventDefault();
                    handleSelectSecondaryColor(color.id);
                  }}
                >
                  <PaletteColorSurface hex={color.hex} />
                  {settings.get["palette.colors"] && (
                    <span aria-hidden="true" className="pallete-color-number">
                      {index}
                    </span>
                  )}
                  {settings.get["palette.symbols"] && (
                    <span
                      aria-hidden="true"
                      className="pallete-color-symbol"
                      style={getPaletteSymbolStyle(index, color.hex)}
                    />
                  )}
                  {hasActiveTemplate &&
                    settings.get["template.pixelCounts"] &&
                    needed > 0 && (
                      <span aria-hidden="true" className="pallete-color-needed">
                        {needed}
                      </span>
                    )}
                </motion.button>
              </Tooltip>
            ))}
          </motion.div>
          <Tooltip
            disableAnimation={reduceMotion}
            closeDelay={0}
            content="Pick Color"
            delay={250}
            placement="top"
            classNames={{
              content: "canvas-overlay-card palette-tooltip px-2 py-1 text-xs",
            }}
          >
            <motion.button
              layout={!reduceMotion}
              layoutDependency={compactPalette}
              transition={paletteLayoutTransition}
              aria-label="Pick Color"
              className="pallete-color--pick"
              onClick={() => {
                setPickColorPreview(null);
                setPickColorMode(true);
              }}
            >
              <Pipette aria-hidden="true" size={18} />
            </motion.button>
          </Tooltip>
        </motion.div>
        {userOverlay}
      </motion.div>
    </>
  );
};
